import { NgComponentOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  InjectionToken,
  Type,
  computed,
  inject,
  input,
  model,
  output
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ENVIRONMENT, LazyModuleInjector, ModalService, TranslateLoader, UserService } from '@cfp/core';
import { PortfolioPdfDownloadButton } from '@cfp/domains/portfolio/pdf-download-button';
import { AiButton } from '@cfp/features-shared/ai';
import { NotesButton } from '@cfp/features-shared/notes';
import { ReportBuildingsDownloadButton } from '@cfp/features-shared/portfolio-buildings-roadmap-report';
import { buildReportPdfFileName, mapReportCustomerNameAndBusinessKey } from '@cfp/features-shared/reports/report';
import { TagsPortfolioModal } from '@cfp/features-shared/tags';
import { LabelGroupModule } from '@cfp/shared/ui/label-group';
import { MeasurementArea } from '@cfp/shared/ui/measurement';
import {
  Badge,
  Button,
  ButtonToggle,
  CfpTranslateModule,
  Dropdown,
  Icon,
  LiveRegionStatusUpdaterModule
} from '@cfp/ui';
import { TranslateModule } from '@ngx-translate/core';
import { CustomerByPortfolioService, LabelGroupDTO, PortfolioItemDTO } from 'csr-asset-app';
import { LicenseDTO } from 'csr-login-app';
import { catchError, map, of, switchMap } from 'rxjs';

export const CUSTOM_PORTFOLIO_HEADER_REPORT_BUTTON = new InjectionToken<Type<Component>>(
  'Custom portfolio header report button'
);

@Component({
  selector: 'cfp-portfolio-header',
  templateUrl: './portfolio-header.html',
  styleUrls: ['./portfolio-header.scss'],
  imports: [
    NgComponentOutlet,
    ReportBuildingsDownloadButton,
    LabelGroupModule,
    TranslateModule,
    Button,
    ButtonToggle,
    Icon,
    LiveRegionStatusUpdaterModule,
    MeasurementArea,
    PortfolioPdfDownloadButton,
    AiButton,
    NotesButton,
    Badge,
    Dropdown,
    CfpTranslateModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PortfolioHeader {
  public readonly portfolio = input.required<PortfolioItemDTO | undefined>();
  public readonly showLabelGroup = input<boolean>(true);
  public readonly activeFiltersCount = input<number>(0);
  public readonly labelGroups = input<LabelGroupDTO[]>([]);
  public readonly labelGroup = input<LabelGroupDTO | undefined>();
  public readonly filtersButtonDisabled = input<boolean>(false);

  public readonly showM2 = model<boolean>(false);

  public readonly toggleM2 = output<void>();
  public readonly showFiltersSidePanel = output<void>();
  public readonly showAddBuildingFlow = output<boolean>();
  public readonly clearFilters = output<MouseEvent>();

  protected readonly portfolioId = computed(() => this.portfolio()?.id ?? 0);

  protected readonly environment = inject(ENVIRONMENT);
  protected readonly userService = inject(UserService);
  protected readonly translateLoader = inject(TranslateLoader);
  private readonly modalService = inject(ModalService);
  private readonly customerByPortfolioService = inject(CustomerByPortfolioService);
  private readonly lazyModuleInjector = inject(LazyModuleInjector);

  protected readonly customPortfolioHeaderReportButton: Type<Component> | null = this.lazyModuleInjector.get(
    CUSTOM_PORTFOLIO_HEADER_REPORT_BUTTON
  );

  protected readonly hasPortfolioBuildingsRoadmapReportLicense = toSignal(
    this.userService.hasLicense(LicenseDTO.LicenseTypeEnum.PortfolioBuildingsRoadmapReport),
    { initialValue: false }
  );
  protected readonly hasAILicense = toSignal(this.userService.hasLicense('ai'), { initialValue: false });
  protected readonly hasPortfolioReportLicense = toSignal(this.userService.hasLicense('portfolio_report'), {
    initialValue: false
  });
  protected readonly hasCrremLicense = toSignal(this.userService.hasLicense('crrem'), { initialValue: false });
  protected readonly hasRoadmapLicense = toSignal(this.userService.hasLicense('roadmap'), { initialValue: false });
  protected readonly hasPortfolioNotesLicense = toSignal(this.userService.hasLicense('portfolio_notes'), {
    initialValue: false
  });

  protected readonly hideManageBuildingTags = this.environment.components.hideManageBuildingTags === true;

  protected readonly hasAiTranslationLoaded = toSignal(
    this.translateLoader.loadTranslation('ai').pipe(
      map(() => true),
      catchError(() => of(false))
    ),
    {
      initialValue: false
    }
  );

  private readonly customer = toSignal(
    toObservable(this.portfolioId).pipe(
      switchMap((portfolioId) => {
        if (!portfolioId || this.environment.components.reportShowCustomerBusinessKeyInFooter !== true) {
          return of(undefined);
        }
        return this.customerByPortfolioService.v2CustomerByPortfolioIdGet(portfolioId);
      })
    )
  );

  protected readonly reportUrl = computed(() => `/report/${this.portfolioId()}`);
  protected readonly reportFileName = computed(() => {
    if (this.environment.components.reportShowCustomerBusinessKeyInFooter === true) {
      const customerNameAndBusinessKey = mapReportCustomerNameAndBusinessKey(this.customer(), {
        showBusinessKeyInFooter: true
      });
      if (customerNameAndBusinessKey.name) {
        return buildReportPdfFileName({
          customerNameAndBusinessKey,
          reportKind: 'portfolio'
        });
      }
    }

    const portfolio = this.portfolio();
    const portfolioName = portfolio ? `${portfolio.name}-` : '';
    return `${portfolioName}portfolio-report.pdf`;
  });

  protected readonly url = computed(
    () => `/report/${this.portfolioId()}${this.hasPortfolioBuildingsRoadmapReportLicense() ? '/buildings' : ''}`
  );

  protected showTags(): void {
    this.modalService.showSidePanel(TagsPortfolioModal, {
      initialState: {
        portfolioId: this.portfolioId()
      }
    });
  }
}
